import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useSimpleRole } from "@/hooks/use-simple-role";
import { useUserOrganization } from "@/hooks/use-user-organization";

export interface DeviceGroup {
  id: string;
  organization_id: string | null;
  name: string;
  reference: string | null;
  description: string | null;
  color: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  device_count?: number;
}

export interface CreateDeviceGroupInput {
  name: string;
  organization_id?: string | null;
  reference?: string | null;
  description?: string | null;
  color?: string | null;
  sort_order?: number;
}

export interface UpdateDeviceGroupInput {
  id: string;
  name?: string;
  reference?: string | null;
  description?: string | null;
  color?: string | null;
  sort_order?: number;
}

export const useDeviceGroups = (organizationId?: string | null) => {
  return useQuery({
    queryKey: ["device-groups", organizationId],
    queryFn: async () => {
      let query = supabase
        .from("device_groups")
        .select("*")
        .order("sort_order", { ascending: true })
        .order("name", { ascending: true });

      if (organizationId) {
        query = query.eq("organization_id", organizationId);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching device groups:", error);
        throw error;
      }

      return data as DeviceGroup[];
    },
  });
};

export const useDeviceGroupsWithCounts = () => {
  return useQuery({
    queryKey: ["device-groups-with-counts"],
    queryFn: async () => {
      // Fetch groups
      const { data: groups, error: groupsError } = await supabase
        .from("device_groups")
        .select("*")
        .order("sort_order", { ascending: true })
        .order("name", { ascending: true });

      if (groupsError) {
        console.error("Error fetching device groups:", groupsError);
        throw groupsError;
      }

      // Fetch device counts per group
      const { data: devices, error: devicesError } = await supabase
        .from("device_details")
        .select("group_id");

      if (devicesError) {
        console.error("Error fetching devices for counts:", devicesError);
        throw devicesError;
      }

      // Count devices per group
      const countMap: Record<string, number> = {};
      devices?.forEach((device) => {
        if (device.group_id) {
          countMap[device.group_id] = (countMap[device.group_id] || 0) + 1;
        }
      });

      // Add counts to groups
      const groupsWithCounts = (groups || []).map((group) => ({
        ...group,
        device_count: countMap[group.id] || 0,
      }));

      return groupsWithCounts as DeviceGroup[];
    },
  });
};

export interface ManageableOrg {
  id: string;
  name: string;
  parent_organization_id: string | null;
}

/** Organisations the current user can work with for groups.
 *  Superuser: all organisations. Others: own organisation + its sub-organisations. */
export const useManageableOrganisations = () => {
  const { role } = useSimpleRole();
  const { data: userOrgId } = useUserOrganization();
  const isSuperuser = role === 'superuser';

  return useQuery({
    queryKey: ["group-manageable-orgs", userOrgId, isSuperuser],
    queryFn: async (): Promise<ManageableOrg[]> => {
      const withParent = async (filter?: (q: any) => any) => {
        let q: any = (supabase as any).from("organizations").select("id, name, parent_organization_id").order("name");
        if (filter) q = filter(q);
        const { data, error } = await q;
        if (!error) return (data || []) as ManageableOrg[];
        // parent_organization_id may not exist yet — fall back to id/name only
        let q2: any = supabase.from("organizations").select("id, name").order("name");
        if (filter) q2 = filter(q2);
        const { data: d2 } = await q2;
        return ((d2 || []) as any[]).map((o) => ({ ...o, parent_organization_id: null }));
      };
      if (isSuperuser) return withParent();
      if (!userOrgId) return [];
      return withParent((q) => q.or(`id.eq.${userOrgId},parent_organization_id.eq.${userOrgId}`));
    },
    enabled: isSuperuser || !!userOrgId,
  });
};

/** Groups of a single organisation (used on a camera for its own organisation). */
export const useDeviceGroupsForOrg = (orgId: string | null | undefined) => {
  return useQuery({
    queryKey: ["device-groups-scoped", "org", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("device_groups")
        .select("*")
        .eq("organization_id", orgId!)
        .order("sort_order", { ascending: true })
        .order("name", { ascending: true });
      if (error) throw error;
      return data as DeviceGroup[];
    },
    enabled: !!orgId,
  });
};

// Organization-scoped hook: superusers see all, others see own org + sub-orgs
export const useOrganizationScopedDeviceGroups = () => {
  const { role } = useSimpleRole();
  const { data: userOrgId } = useUserOrganization();
  const { data: orgs = [] } = useManageableOrganisations();
  const isSuperuser = role === 'superuser';
  const orgIds = isSuperuser ? [] : orgs.map((o) => o.id);
  if (!isSuperuser && userOrgId && !orgIds.includes(userOrgId)) orgIds.push(userOrgId);

  return useQuery({
    queryKey: ["device-groups-scoped", userOrgId, isSuperuser, orgIds.join(",")],
    queryFn: async () => {
      let query = supabase
        .from("device_groups")
        .select("*")
        .order("sort_order", { ascending: true })
        .order("name", { ascending: true });

      if (!isSuperuser) {
        query = query.in("organization_id", orgIds);
      }

      const { data, error } = await query;
      if (error) {
        console.error("Error fetching scoped device groups:", error);
        throw error;
      }
      return data as DeviceGroup[];
    },
    enabled: isSuperuser || !!userOrgId,
  });
};

// Organization-scoped hook with device counts
export const useOrganizationScopedDeviceGroupsWithCounts = (selectedOrgId?: string | null) => {
  const { role } = useSimpleRole();
  const { data: userOrgId } = useUserOrganization();
  const { data: orgs = [] } = useManageableOrganisations();
  const isSuperuser = role === 'superuser';
  const familyIds = orgs.map((o) => o.id);
  if (userOrgId && !familyIds.includes(userOrgId)) familyIds.push(userOrgId);

  return useQuery({
    queryKey: ["device-groups-scoped-with-counts", userOrgId, isSuperuser, selectedOrgId ?? "all", familyIds.join(",")],
    queryFn: async () => {
      // Fetch groups - scoped by org for non-superusers
      let groupsQuery = supabase
        .from("device_groups")
        .select("*")
        .order("sort_order", { ascending: true })
        .order("name", { ascending: true });

      if (selectedOrgId) {
        groupsQuery = groupsQuery.eq("organization_id", selectedOrgId);
      } else if (!isSuperuser) {
        groupsQuery = groupsQuery.in("organization_id", familyIds);
      }

      const { data: groups, error: groupsError } = await groupsQuery;

      if (groupsError) {
        console.error("Error fetching scoped device groups:", groupsError);
        throw groupsError;
      }

      // Fetch device counts per group
      const { data: devices, error: devicesError } = await supabase
        .from("device_details")
        .select("group_id");

      if (devicesError) {
        console.error("Error fetching devices for counts:", devicesError);
        throw devicesError;
      }

      // Count devices per group
      const countMap: Record<string, number> = {};
      devices?.forEach((device) => {
        if (device.group_id) {
          countMap[device.group_id] = (countMap[device.group_id] || 0) + 1;
        }
      });

      // Add counts to groups
      const groupsWithCounts = (groups || []).map((group) => ({
        ...group,
        device_count: countMap[group.id] || 0,
      }));

      return groupsWithCounts as DeviceGroup[];
    },
    enabled: isSuperuser || !!userOrgId,
  });
};

export const useCreateDeviceGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateDeviceGroupInput) => {
      const { data: { user } } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from("device_groups")
        .insert({
          name: input.name,
          organization_id: input.organization_id || null,
          reference: input.reference || null,
          description: input.description || null,
          color: input.color || null,
          sort_order: input.sort_order || 0,
          created_by: user?.id || null,
        })
        .select()
        .single();

      if (error) {
        console.error("Error creating device group:", error);
        throw error;
      }

      return data as DeviceGroup;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["device-groups"] });
      queryClient.invalidateQueries({ queryKey: ["device-groups-with-counts"] });
      queryClient.invalidateQueries({ queryKey: ["device-groups-scoped"] });
      queryClient.invalidateQueries({ queryKey: ["device-groups-scoped-with-counts"] });
      toast.success("Device group created successfully");
    },
    onError: (error: any) => {
      if (error.code === "23505") {
        toast.error("A group with this name already exists");
      } else {
        toast.error("Failed to create device group");
      }
    },
  });
};

export const useUpdateDeviceGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateDeviceGroupInput) => {
      const updates: Partial<DeviceGroup> = {};
      if (input.name !== undefined) updates.name = input.name;
      if (input.reference !== undefined) updates.reference = input.reference;
      if (input.description !== undefined) updates.description = input.description;
      if (input.color !== undefined) updates.color = input.color;
      if (input.sort_order !== undefined) updates.sort_order = input.sort_order;

      const { data, error } = await supabase
        .from("device_groups")
        .update(updates)
        .eq("id", input.id)
        .select()
        .single();

      if (error) {
        console.error("Error updating device group:", error);
        throw error;
      }

      return data as DeviceGroup;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["device-groups"] });
      queryClient.invalidateQueries({ queryKey: ["device-groups-with-counts"] });
      queryClient.invalidateQueries({ queryKey: ["device-groups-scoped"] });
      queryClient.invalidateQueries({ queryKey: ["device-groups-scoped-with-counts"] });
      toast.success("Device group updated successfully");
    },
    onError: (error: any) => {
      if (error.code === "23505") {
        toast.error("A group with this name already exists");
      } else {
        toast.error("Failed to update device group");
      }
    },
  });
};

export const useDeleteDeviceGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupId: string) => {
      // First unassign all devices from this group
      const { error: updateError } = await supabase
        .from("device_details")
        .update({ group_id: null, group_name: null })
        .eq("group_id", groupId);

      if (updateError) {
        console.error("Error unassigning devices from group:", updateError);
        throw updateError;
      }

      const { error } = await supabase
        .from("device_groups")
        .delete()
        .eq("id", groupId);

      if (error) {
        console.error("Error deleting device group:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["device-groups"] });
      queryClient.invalidateQueries({ queryKey: ["device-groups-with-counts"] });
      queryClient.invalidateQueries({ queryKey: ["device-groups-scoped"] });
      queryClient.invalidateQueries({ queryKey: ["device-groups-scoped-with-counts"] });
      queryClient.invalidateQueries({ queryKey: ["device-assignments"] });
      toast.success("Device group deleted successfully");
    },
    onError: () => {
      toast.error("Failed to delete device group");
    },
  });
};

export const useAssignDeviceToGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      deviceId, 
      groupId, 
      groupName 
    }: { 
      deviceId: string; 
      groupId: string | null; 
      groupName: string | null;
    }) => {
      const { error } = await supabase
        .from("device_details")
        .update({ 
          group_id: groupId,
          group_name: groupName // Keep in sync for BuildShip API
        })
        .eq("device_id", deviceId);

      if (error) {
        console.error("Error assigning device to group:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["device-groups"] });
      queryClient.invalidateQueries({ queryKey: ["device-groups-with-counts"] });
      queryClient.invalidateQueries({ queryKey: ["device-groups-scoped"] });
      queryClient.invalidateQueries({ queryKey: ["device-groups-scoped-with-counts"] });
      queryClient.invalidateQueries({ queryKey: ["device-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["device-details"] });
    },
    onError: () => {
      toast.error("Failed to assign device to group");
    },
  });
};
